import { useCallback, useId, useLayoutEffect, useMemo, useRef, useState } from "react"
import { Search } from "@mui/icons-material"
import { Alert, Box, Button, Card, CardActionArea, Chip, CircularProgress, InputAdornment, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { useUserRole } from "hooks/users"
import type { SkillCatalogSkill, SkillSection, SkillsLibraryTab } from "connected-components/skills/types"
import { canManageSkillShare, canShareSkill } from "connected-components/skills/utils/tier"
import { SHARED_SECTION_TITLE, filterSkillSections, getAvailableFacets } from "connected-components/skills/utils/sections"
import { MessageTime } from "connected-components/agent-chat/chatbox/message-time"
import { SkillShareButton } from "./skill-share-button"
import { SKILLS_BODY_PADDING } from "./kelly-skills-page"
import { formatSharedBy, formatSkillMeta } from "./skill-meta"
import { UploadSkillDialog } from "./upload-skill-dialog"

// The tabs, the search field and the category chips are one filter bar, so they stay reachable while the
// list scrolls under them. The negative margins cancel the scroll container's padding (kelly-skills-page
// BODY_SX p:2) so the bar spans the full width and sits flush at the top; the padding puts it back inside.
// A whole-object callback, not a per-value one: sx resolves a function passed as the sx prop, but leaves a
// function sitting on a single property unresolved, which silently drops that property.
//
// The margins and padding stay as sx spacing keys so MUI negates them itself. Do NOT interpolate
// `-${theme.spacing(n)}`: with cssVariables that returns `calc(n * var(--mui-spacing))`, so the minus sign
// yields `-calc(...)`, which is invalid CSS and is dropped without warning. `top` needs the theme, and is
// not an sx spacing key, so it gets an explicit calc.
const FILTER_BAR_SX = (theme: Theme) => ({
  position: "sticky",
  // Negative by exactly the body's padding: sticky `top` resolves against the scroll container's PADDING
  // box, so top:0 pins the bar one padding-step below the header and leaves a gap it scrolls through.
  top: `calc(-1 * ${theme.spacing(SKILLS_BODY_PADDING)})`,
  zIndex: 2,
  bgcolor: "var(--app-bg-paper)",
  mx: -SKILLS_BODY_PADDING,
  mt: -SKILLS_BODY_PADDING,
  px: SKILLS_BODY_PADDING,
  pt: SKILLS_BODY_PADDING,
  // A hairline, not a shadow: the bar and the page share one paper colour, and a shadow needs a lighter
  // ground than the thing casting it to read at all. Same token and weight as the page header's rule, and
  // it composites over the same background, so the two lines land on the identical grey.
  borderBottom: 1,
  borderColor: "divider",
  // Without this the first group label sits flush against the hairline.
  mb: SKILLS_BODY_PADDING,
})
const TOOLBAR_SX = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, pb: 2 } satisfies SxProps<Theme>
const CATEGORY_FILTERS_SX = { display: "flex", flexWrap: "wrap", gap: 1, pb: 2 } satisfies SxProps<Theme>
const TABS_SX = {
  gap: 0.5,
  "& .MuiToggleButton-root": { border: 1, borderColor: "divider", borderRadius: 999, px: 2, py: 0.5, textTransform: "none" },
  // Selected reads as the primary action, matching the picked category chips below the toolbar; MUI's default
  // selected ToggleButton is a neutral grey that sits a shade off the unselected border in dark mode.
  "& .MuiToggleButton-root.Mui-selected": {
    bgcolor: "var(--app-primary-filled)",
    color: "var(--app-text-on-dark-bg)",
    borderColor: "var(--app-primary-filled)",
    "&:hover": { bgcolor: "var(--app-primary-filled)" },
  },
} satisfies SxProps<Theme>
// The tabs are pills because they are a segmented control; the search field keeps the theme's input shape.
// It grows to fill the toolbar but stops at a field-sized width — flex:1 alone stretched it the full width of
// a full-screen dock, far past anything a skill name or description query needs.
const SEARCH_SX = { flex: 1, minWidth: 180, maxWidth: 320 } satisfies SxProps<Theme>
const SECTION_SX = { display: "flex", flexDirection: "column", gap: 1.5, mb: 3 } satisfies SxProps<Theme>
const SECTION_LABEL_SX = { display: "flex", alignItems: "center", gap: 1 } satisfies SxProps<Theme>
const SECTION_TITLE_SX = { fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", px: 1, py: 0.25, borderRadius: 1, bgcolor: "action.selected", color: "text.secondary", m: 0 } satisfies SxProps<Theme>
const SECTION_COUNT_SX = { fontSize: "0.875rem", color: "text.secondary" } satisfies SxProps<Theme>
const GRID_SX = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 2 } satisfies SxProps<Theme>
// Show more, the enable switch and the download button all sit outside the CardActionArea, so its own hover
// highlight stops short of them and the card lights up in two pieces. The hover belongs to the card; the
// action area's is suppressed on hover only, leaving its focus-visible highlight intact for the keyboard.
const CARD_SX = {
  position: "relative",
  borderRadius: 2,
  display: "flex",
  flexDirection: "column",
  "&:hover": { bgcolor: "action.hover" },
  "& .MuiCardActionArea-root:hover .MuiCardActionArea-focusHighlight": { opacity: 0 },
} satisfies SxProps<Theme>
const CARD_DISABLED_SX = { ...CARD_SX, bgcolor: "action.hover", "& .MuiCardActionArea-root": { opacity: 0.55 } } satisfies SxProps<Theme>
const CARD_BODY_SX = { display: "flex", flexDirection: "column", alignItems: "stretch", gap: 1.5, p: 2, pr: 9, flex: 1 } satisfies SxProps<Theme>
const TITLE_ROW_SX = { display: "flex", alignItems: "center", gap: 1.5 } satisfies SxProps<Theme>
const NAME_SX = { display: "inline-flex", alignItems: "center", gap: 1, fontWeight: 600 } satisfies SxProps<Theme>
const DESCRIPTION_LINES = 2
// Clamped in CSS rather than cut from the string, so "Show more" has the rest of the text to reveal.
// minHeight keeps two-line and one-line cards the same height across a grid row. No flex grow here: the clamp
// check compares scrollHeight to clientHeight, and a box stretched by a taller neighbour would read as unclipped.
// The footer's auto top margin is what fills a stretched card instead.
const DESCRIPTION_SX = {
  color: "text.secondary",
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: DESCRIPTION_LINES,
  overflow: "hidden",
  minHeight: `calc(${DESCRIPTION_LINES} * 1.43em)`,
} satisfies SxProps<Theme>
const DESCRIPTION_EXPANDED_SX = { color: "text.secondary" } satisfies SxProps<Theme>
// Outside the CardActionArea: a button inside a button is invalid, and the whole body is the card's own
// click target. pr clears the download control's lane.
const EXPAND_SX = { alignSelf: "flex-start", textTransform: "none", ml: 1, mb: 0.5, mr: 9 } satisfies SxProps<Theme>
const FOOTER_SX = { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.75, mt: "auto" } satisfies SxProps<Theme>
const META_SX = { fontSize: "0.75rem", color: "text.secondary" } satisfies SxProps<Theme>
const SWITCH_SX = { position: "absolute", top: 12, right: 12 } satisfies SxProps<Theme>
const ACTIONS_SX = { position: "absolute", bottom: 8, right: 8 } satisfies SxProps<Theme>
const NO_MATCH_SX = { color: "text.secondary", py: 1.5 } satisfies SxProps<Theme>

const NO_CHIPS: string[] = []
const BASE_FILTERS = { showPreview: true } as const
const TIER_TAB_LABEL: Record<Exclude<SkillsLibraryTab, "all" | "yours" | "shared">, string> = { core: "Core", tailored: "Tailored" }
const SHARED_LOAD_ERROR_TEXT = "Couldn't load skills shared with you. Please try again later."

function emptyMessage(tab: SkillsLibraryTab, query: string, ownFailed: boolean): string {
  const trimmed = query.trim()
  if (tab === "yours" && ownFailed) return "Your own skills did not load. Try again later."
  if (trimmed) return `No skills match "${trimmed}"`
  if (tab === "all") return "No skills are published to this account."
  if (tab === "yours") return "You have not added any skills of your own yet. Ask Kelly to build one, or upload a .zip or .skill file."
  if (tab === "shared") return "No skills are shared with you yet."
  return `No ${TIER_TAB_LABEL[tab]} skills are published to this account.`
}

export interface SkillListProps {
  sections: SkillSection[]
  categoryOrder: string[]
  ownSkillsFailed?: boolean
  sharedSkillsFailed?: boolean
  sharedSkillsLoading?: boolean
  isSkillEnabled: (skillId: string) => boolean
  onToggle: (skill: SkillCatalogSkill, enabled: boolean) => void
  pendingSkillIds: ReadonlySet<string>
  onSelect?: (skill: SkillCatalogSkill) => void
}

interface SkillCardProps {
  skill: SkillCatalogSkill
  enabled: boolean
  isSaving: boolean
  onToggle: (skill: SkillCatalogSkill, enabled: boolean) => void
  onSelect?: (skill: SkillCatalogSkill) => void
}

function SkillCard({ skill, enabled, isSaving, onToggle, onSelect }: SkillCardProps) {
  const nameId = useId()
  const descriptionId = useId()
  const role = useUserRole()
  const canManage = canManageSkillShare(skill, role)
  const select = useCallback(() => onSelect?.(skill), [onSelect, skill])
  const descriptionRef = useRef<HTMLParagraphElement | null>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [isClamped, setIsClamped] = useState(false)
  const toggleExpanded = useCallback(() => setIsExpanded((current) => !current), [])

  // Whether the clamp is actually hiding anything depends on the rendered width, which changes with the dock
  // view and the grid's auto-fill — so it is measured, not guessed from the character count. Only measured
  // while collapsed: expanded, scrollHeight equals clientHeight and the control would remove itself.
  useLayoutEffect(() => {
    const el = descriptionRef.current
    if (!el || isExpanded) return undefined
    const measure = () => setIsClamped(el.scrollHeight > el.clientHeight + 1)
    measure()
    if (typeof ResizeObserver === "undefined") return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [skill.description, isExpanded])
  return (
    <Card
      variant="outlined"
      sx={enabled ? CARD_SX : CARD_DISABLED_SX}
      data-testid="kelly-skill-row"
      data-enabled={enabled ? "true" : "false"}
    >
      <CardActionArea sx={CARD_BODY_SX} aria-labelledby={nameId} onClick={skill.manageOnly ? undefined : select}>
        <Box sx={TITLE_ROW_SX}>
          <Typography component="span" sx={NAME_SX}>
            <span id={nameId} data-testid="kelly-skill-name">{skill.name}</span>
            {skill.beta && <Chip size="small" label="Preview" />}
          </Typography>
        </Box>
        <Typography
          ref={descriptionRef}
          id={descriptionId}
          variant="body2"
          sx={isExpanded ? DESCRIPTION_EXPANDED_SX : DESCRIPTION_SX}
          data-testid="kelly-skill-description"
        >
          {skill.description}
        </Typography>
        <Box sx={FOOTER_SX}>
          {skill.categories?.map((category) => (
            <Chip key={category} size="small" variant="outlined" label={category} data-testid="kelly-skill-category" />
          ))}
          <Typography component="span" sx={META_SX} data-testid="kelly-skill-meta">
            {skill.tier === "shared"
              ? <>{formatSharedBy(skill)}{skill.lastUpdated && <>{" · "}<MessageTime date={skill.lastUpdated} /></>}</>
              : formatSkillMeta(skill)}
          </Typography>
        </Box>
      </CardActionArea>
      {(isClamped || isExpanded) && (
        <Button size="small" sx={EXPAND_SX} onClick={toggleExpanded} aria-expanded={isExpanded} aria-controls={descriptionId} data-testid="kelly-skill-expand">
          {isExpanded ? "Show less" : "Show more"}
        </Button>
      )}
      {!skill.manageOnly && (
        <Switch
          sx={SWITCH_SX}
          color="primary"
          checked={enabled}
          disabled={isSaving}
          onChange={(_, checked) => onToggle(skill, checked)}
          slotProps={{ input: { "aria-label": `Use ${skill.name}` } }}
        />
      )}
      {(canShareSkill(skill) || canManage) && (
        <Box sx={ACTIONS_SX}>
          {canShareSkill(skill) && <SkillShareButton skill={skill} />}
          {canManage && <SkillShareButton skill={skill} mode="manage" />}
        </Box>
      )}
    </Card>
  )
}

export function SkillList({ sections, categoryOrder, ownSkillsFailed = false, sharedSkillsFailed = false, sharedSkillsLoading = false, isSkillEnabled, onToggle, pendingSkillIds, onSelect }: SkillListProps) {
  const [tab, setTab] = useState<SkillsLibraryTab>("all")
  const [query, setQuery] = useState("")
  const [categories, setCategories] = useState<string[]>(NO_CHIPS)
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false)
  const openUploadDialog = useCallback(() => setIsUploadDialogOpen(true), [])
  const closeUploadDialog = useCallback(() => setIsUploadDialogOpen(false), [])
  const availableCategories = useMemo(
    () => getAvailableFacets(sections, { tab, query, ...BASE_FILTERS }, categoryOrder).categories,
    [sections, tab, query, categoryOrder],
  )
  // A picked category that the tab or search has since put out of scope still counts, so the chip stays
  // pressed and the list stays narrowed until the user releases it.
  const activeCategories = useMemo(
    () => categories.filter((category) => availableCategories.includes(category)),
    [categories, availableCategories],
  )
  const visibleSections = useMemo(
    () => filterSkillSections(sections, { tab, query, roles: NO_CHIPS, categories: activeCategories, ...BASE_FILTERS }),
    [sections, tab, query, activeCategories],
  )
  const isSharedSlotOpen = (tab === "all" || tab === "shared") && !visibleSections.some((section) => section.id === "shared")
  const showSharedError = sharedSkillsFailed && isSharedSlotOpen
  const showSharedLoading = sharedSkillsLoading && !sharedSkillsFailed && isSharedSlotOpen
  const sectionsWithSharedError = useMemo(() => {
    if (!showSharedError && !showSharedLoading) return visibleSections
    const errorSection: SkillSection = { id: "shared", title: SHARED_SECTION_TITLE, skills: [] }
    const yoursIndex = visibleSections.findIndex((section) => section.id === "yours")
    const insertAt = yoursIndex === -1 ? 0 : yoursIndex + 1
    return [...visibleSections.slice(0, insertAt), errorSection, ...visibleSections.slice(insertAt)]
  }, [visibleSections, showSharedError, showSharedLoading])
  const toggleCategory = useCallback((category: string) => {
    setCategories((current) => (current.includes(category) ? current.filter((entry) => entry !== category) : [...current, category]))
  }, [])

  return (
    <Box data-testid="kelly-skill-list">
      <Box sx={FILTER_BAR_SX} data-testid="kelly-skill-filter-bar">
        <Box sx={TOOLBAR_SX}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={tab}
            aria-label="Skill tier"
            sx={TABS_SX}
            onChange={(_, value: SkillsLibraryTab | null) => { if (value) setTab(value) }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="core">Core</ToggleButton>
            <ToggleButton value="tailored">Tailored</ToggleButton>
            <ToggleButton value="yours">Yours</ToggleButton>
            <ToggleButton value="shared">{SHARED_SECTION_TITLE}</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            type="search"
            placeholder="Search skills"
            value={query}
            sx={SEARCH_SX}
            onChange={(event) => setQuery(event.target.value)}
            slotProps={{
              htmlInput: { "aria-label": "Search skills" },
              input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> },
            }}
          />
          {tab === "yours" && (
            <Button size="small" variant="outlined" onClick={openUploadDialog} data-testid="kelly-skill-upload-toolbar-button">
              Upload a skill
            </Button>
          )}
        </Box>
        {availableCategories.length > 0 && (
          <Box role="group" aria-label="Skill categories" sx={CATEGORY_FILTERS_SX}>
            {availableCategories.map((category) => {
              const isActive = activeCategories.includes(category)
              return (
                <Chip
                  key={category}
                  size="small"
                  label={category}
                  color={isActive ? "primary" : "default"}
                  variant={isActive ? "filled" : "outlined"}
                  aria-pressed={isActive}
                  onClick={() => toggleCategory(category)}
                />
              )
            })}
          </Box>
        )}
      </Box>
      {sectionsWithSharedError.length === 0 && (
        <>
          <Typography sx={NO_MATCH_SX} data-testid="kelly-skill-list-empty">{emptyMessage(tab, query, ownSkillsFailed)}</Typography>
          {tab === "yours" && query.trim() === "" && !ownSkillsFailed && (
            <Button size="small" variant="outlined" onClick={openUploadDialog} data-testid="kelly-skill-upload-empty-button">
              Upload a skill
            </Button>
          )}
        </>
      )}
      {sectionsWithSharedError.map((section) => (
        <Box component="section" key={section.id} sx={SECTION_SX} aria-label={section.title} data-testid={`kelly-skill-section-${section.title.toLowerCase()}`}>
          <Box sx={SECTION_LABEL_SX}>
            <Typography component="h3" sx={SECTION_TITLE_SX}>{section.title}</Typography>
            <Typography component="span" sx={SECTION_COUNT_SX}>{section.skills.length}</Typography>
          </Box>
          {section.id === "shared" && section.skills.length === 0 && showSharedError
            ? <Alert severity="error">{SHARED_LOAD_ERROR_TEXT}</Alert>
            : section.id === "shared" && section.skills.length === 0 && showSharedLoading
              ? <CircularProgress size={20} aria-label="Loading shared skills" />
              : (
              <Box sx={GRID_SX}>
                {section.skills.map((skill) => (
                  <SkillCard
                    key={skill.id}
                    skill={skill}
                    enabled={isSkillEnabled(skill.id)}
                    isSaving={pendingSkillIds.has(skill.id)}
                    onToggle={onToggle}
                    onSelect={onSelect}
                  />
                ))}
              </Box>
            )}
        </Box>
      ))}
      {isUploadDialogOpen && <UploadSkillDialog open onClose={closeUploadDialog} />}
    </Box>
  )
}
